import { importProvidersFrom } from '@angular/core'
import { Routes } from '@angular/router'

import { EffectsModule } from '@ngrx/effects'

import { AiAgent } from '@flowx/designer/modules/new-ai/common/ai.dictionary'
import { MediaLibraryNominalApi } from '@flowx/designer/shared/api/media-library-nominal.api'
import { resourcePage } from '@flowx/designer/shared/entities/resource-page.entity'
import { DefaultApplicationPermissionsGuard } from '@flowx/designer/shared/guards/application-permissions.guards'
import {
  ApplicationConfigurationparametersPermissionsResolver,
  ApplicationDependenciesPermissionsResolver,
  ApplicationDocumentTemplatesPermissionsResolver,
  ApplicationEnumerationsPermissionsResolver,
  ApplicationMediaLibraryPermissionsResolver,
  ApplicationNotificationTemplatesPermissionsResolver,
  ApplicationProcessesPermissionsResolver,
  ApplicationSettingsPermissionsResolver,
  ApplicationTaskAllocationRulesPermissionsResolver,
  ApplicationTaskStagesPermissionsResolver,
  ApplicationTaskViewsPermissionsResolver,
} from '@flowx/designer/shared/guards/application-permissions.resolvers'
import { applicationSettingsCanDeactivate } from '@flowx/designer/shared/guards/application-settings.guards'
import * as fromStore from '@flowx/designer/shared/store'
import { LayoutOptions } from '@flowx/designer/shared/types/layout.types'

import { EnumerationValuesComponent } from '../cms/pages/enumeration-values/enumeration-values.component'
import { SubstitutionTagsComponent } from '../cms/pages/substitution-tags/substitution-tags.component'
import { MEDIA_LIBRARY_API } from '../core/dictionary/media-library.dictionary'
import { dependenciesCanDeactivate } from '../dependencies/guards/dependencies.guard'

export const ApplicationResourcesRoutes: Routes = [
  {
    path: '',
    redirectTo: 'processes',
    pathMatch: 'full',
  },
  {
    path: 'processes',
    data: resourcePage({ type: 'process' }),
    canActivateChild: [DefaultApplicationPermissionsGuard('process_read')],
    resolve: { accessRights: ApplicationProcessesPermissionsResolver },
    children: [
      {
        path: '',
        data: {
          [LayoutOptions.HAS_SIDENAV]: true,
          location: 'PROJECTS->PROCESSES',
        },
        loadComponent: () =>
          import('@flowx/designer/modules/projects/components/processes-list/processes-list.component').then(
            ({ FlxProcessesListComponent }) => FlxProcessesListComponent
          ),
      },
      {
        path: ':selectedProcessResourceId',
        data: {
          ...resourcePage({ type: 'process', idParam: 'selectedProcessResourceId' }),
          location: 'PROJECTS->PROCESSES->EXISTING_PROCESS',
        },
        loadChildren: () =>
          import('@flowx/designer/modules/process-details/process-details.module').then(
            (m) => m.ProcessDetailsModule
          ),
      },
    ],
  },
  {
    path: 'ui-flows',
    data: {
      ...resourcePage({ type: 'ui-flow' }),
      ...resourcePage({ type: 'ui-flow' }),
      location: 'PROJECTS->UI_FLOWS',
    },
    loadChildren: () =>
      import('@flowx/designer/modules/ui-flow/ui-flow-routes').then(
        ({ UiFlowRoutes }) => UiFlowRoutes
      ),
  },
  {
    path: 'craft-ui',
    data: { [LayoutOptions.HAS_SIDENAV]: false, location: 'PROJECTS->VIBE_UI' },
    loadComponent: () =>
      import('@flowx/designer/modules/vibe-ui/vibe-ui-page.component').then(
        (m) => m.VibeUiPageComponent
      ),
  },
  {
    path: 'craft-ui/:sessionId',
    data: { [LayoutOptions.HAS_SIDENAV]: false, location: 'PROJECTS->VIBE_UI' },
    loadComponent: () =>
      import('@flowx/designer/modules/vibe-ui/vibe-ui-page.component').then(
        (m) => m.VibeUiPageComponent
      ),
  },
  {
    path: 'craft-ui/:sessionId/preview',
    data: { [LayoutOptions.HAS_SIDENAV]: false, location: 'PROJECTS->VIBE_UI_PREVIEW' },
    loadComponent: () =>
      import('@flowx/designer/modules/vibe-ui/vibe-ui-preview-page.component').then(
        (m) => m.VibeUiPreviewPageComponent
      ),
  },
  {
    path: 'reusable-resources',
    data: { location: 'PROJECTS->REUSABLE_RESOURCES' },
    loadChildren: () =>
      import('@flowx/designer/modules/reusable-resources/reusable-resources.routes').then(
        ({ ReusableResourcesRoutes }) => ReusableResourcesRoutes
      ),
  },
  {
    path: 'enumerations',
    data: resourcePage({ type: 'enumeration' }),
    canActivateChild: [DefaultApplicationPermissionsGuard('enum_read')],
    resolve: { accessRights: ApplicationEnumerationsPermissionsResolver },
    children: [
      {
        path: '',
        data: {
          [LayoutOptions.HAS_SIDENAV]: true,
          location: 'PROJECTS->CMS->ENUMERATIONS',
        },
        loadComponent: () =>
          import('@flowx/designer/modules/projects/components/enumerations-list/enumerations-list.component').then(
            ({ FlxEnumerationsListComponent }) => FlxEnumerationsListComponent
          ),
      },
      {
        path: 'values',
        data: {
          ...resourcePage({ type: 'enumeration', enumerationPath: true }),
          location: 'PROJECTS->CMS->EXISTING_ENUMERATION',
        },
        component: EnumerationValuesComponent,
      },
    ],
  },
  {
    path: 'substitution-tags',
    data: resourcePage({ type: 'substitutiontag' }),
    children: [
      {
        path: '',
        data: {
          [LayoutOptions.HAS_SIDENAV]: true,
          location: 'PROJECTS->CMS->EXISTING_SUBSTITUTION_TAG',
        },
        canActivate: [DefaultApplicationPermissionsGuard('enum_read')],
        resolve: { accessRights: ApplicationEnumerationsPermissionsResolver },
        component: SubstitutionTagsComponent,
      },
    ],
  },
  {
    path: 'media-library',
    // List context only: a media preview is not an explicit resource selection.
    canActivate: [DefaultApplicationPermissionsGuard('proj_mediafile_read')],
    providers: [{ provide: MEDIA_LIBRARY_API, useClass: MediaLibraryNominalApi }],
    data: {
      ...resourcePage({ type: 'mediafile' }),
      [LayoutOptions.HAS_SIDENAV]: true,
      location: 'PROJECTS->CMS->MEDIA_LIBRARY',
    },
    resolve: { accessRights: ApplicationMediaLibraryPermissionsResolver },
    loadComponent: () =>
      import('@flowx/designer/modules/cms/pages/media-library/media-library.component').then(
        ({ MediaLibraryComponent }) => MediaLibraryComponent
      ),
  },
  {
    path: 'files',
    canActivate: [DefaultApplicationPermissionsGuard('proj_mediafile_read')],
    data: { [LayoutOptions.HAS_SIDENAV]: true, location: 'PROJECTS->CMS->FILES' },
    resolve: { accessRights: ApplicationMediaLibraryPermissionsResolver },
    loadComponent: () =>
      import('@flowx/designer/modules/cms/pages/files/files.component').then(
        ({ FlxCmsFilesComponent }) => FlxCmsFilesComponent
      ),
  },
  {
    path: 'configurationParameters',
    canActivate: [DefaultApplicationPermissionsGuard('config_param_read')],
    resolve: {
      accessRights: ApplicationConfigurationparametersPermissionsResolver,
    },
    data: {
      ...resourcePage({ type: 'configParams' }),
      [LayoutOptions.HAS_SIDENAV]: true,
      location: 'PROJECTS->CONFIGURATION_PARAMETERS',
    },
    loadComponent: () =>
      import('@flowx/designer/modules/projects/components/configuration-parameters/configuration-parameters.component').then(
        ({ FlxConfigurationParametersComponent }) => FlxConfigurationParametersComponent
      ),
  },
  {
    path: 'dependencies',
    data: {
      ...resourcePage({ type: 'dependencies' }),
      [LayoutOptions.HAS_SIDENAV]: true,
      location: 'PROJECTS->DEPENDENCIES',
    },
    canActivate: [DefaultApplicationPermissionsGuard('dependency_read')],
    resolve: { accessRights: ApplicationDependenciesPermissionsResolver },
    canDeactivate: [dependenciesCanDeactivate],
    loadComponent: () =>
      import('@flowx/designer/modules/dependencies/components/dependencies-page/dependencies-page.component').then(
        ({ DependenciesPageComponent }) => DependenciesPageComponent
      ),
  },
  {
    path: 'settings',
    canActivate: [DefaultApplicationPermissionsGuard('project_read')],
    resolve: { accessRights: ApplicationSettingsPermissionsResolver },
    data: {
      ...resourcePage({ type: 'applicationSettings' }),
      [LayoutOptions.HAS_SIDENAV]: true,
      location: 'PROJECTS->PROJECT_SETTINGS',
    },
    loadComponent: () =>
      import('@flowx/designer/modules/settings/application-settings.component').then(
        ({ FlxApplicationSettingsComponent }) => FlxApplicationSettingsComponent
      ),
    canDeactivate: [applicationSettingsCanDeactivate],
  },
  {
    path: 'document-intelligence',
    canActivate: [DefaultApplicationPermissionsGuard('proj_mediafile_read')],
    resolve: { accessRights: ApplicationMediaLibraryPermissionsResolver },
    data: {
      [LayoutOptions.HAS_SIDENAV]: true,
      location: 'PROJECTS->DOCUMENT_INTELLIGENCE',
    },
    loadComponent: () =>
      import('@flowx/designer/modules/ai/modules/documents/components/ai-document-intelligence/ai-document-intelligence.component').then(
        ({ AiDocumentIntelligenceComponent }) => AiDocumentIntelligenceComponent
      ),
  },
  {
    path: 'views',
    canActivate: [DefaultApplicationPermissionsGuard('task_view_read')],
    resolve: { accessRights: ApplicationTaskViewsPermissionsResolver },
    // HAS_ISSUES_PANEL belongs on the list leaf inside the lazy routes: route data is inherited,
    // so declaring it here would give the panel to the view editor and its children too.
    data: {
      ...resourcePage({ type: 'view' }),
      [LayoutOptions.HAS_SIDENAV]: true,
      location: 'PROJECTS->TASK_MANAGEMENT->VIEWS',
    },
    loadChildren: () =>
      import('@flowx/designer/modules/views/views.module').then((m) => m.ViewsModule),
  },
  {
    path: 'stages',
    canActivate: [DefaultApplicationPermissionsGuard('stage_read')],
    resolve: { accessRights: ApplicationTaskStagesPermissionsResolver },
    data: {
      ...resourcePage({ type: 'stage' }),
      [LayoutOptions.HAS_SIDENAV]: true,
      location: 'PROJECTS->TASK_MANAGEMENT->STAGES',
    },
    loadChildren: () =>
      import('@flowx/designer/modules/stages/stages.module').then((m) => m.StagesModule),
  },
  {
    path: 'allocations',
    canActivate: [DefaultApplicationPermissionsGuard('allocation_rule_read')],
    resolve: { accessRights: ApplicationTaskAllocationRulesPermissionsResolver },
    data: {
      ...resourcePage({ type: 'allocation-setting' }),
      [LayoutOptions.HAS_SIDENAV]: true,
      location: 'PROJECTS->TASK_MANAGEMENT->ALLOCATIONS_RULES',
    },
    loadChildren: () =>
      import('@flowx/designer/modules/allocations/allocations.module').then(
        (m) => m.AllocationsModule
      ),
  },
  {
    path: 'notification-templates',
    canActivate: [DefaultApplicationPermissionsGuard('notification_templates_read')],
    resolve: { accessRights: ApplicationNotificationTemplatesPermissionsResolver },
    data: {
      ...resourcePage({ type: 'notification-template' }),
      [LayoutOptions.HAS_SIDENAV]: true,
      location: 'PROJECTS->CMS->EXISTING_NOTIFICATION_TEMPLATE',
    },
    loadChildren: () =>
      import('@flowx/designer/modules/notification-templates/notification-templates.module').then(
        (m) => m.NotificationTemplatesModule
      ),
  },
  {
    path: 'document-templates',
    canActivate: [DefaultApplicationPermissionsGuard('document_templates_read')],
    resolve: { accessRights: ApplicationDocumentTemplatesPermissionsResolver },
    data: {
      ...resourcePage({ type: 'document-template' }),
      [LayoutOptions.HAS_SIDENAV]: true,
      location: 'PROJECTS->CMS->EXISTING_DOCUMENT_TEMPLATE',
    },
    loadChildren: () =>
      import('@flowx/designer/modules/document-templates/documents.module').then(
        (m) => m.DocumentsModule
      ),
  },
  {
    path: 'integration-designer',
    data: { location: 'PROJECTS->INTEGRATION_DESIGNER' },
    loadChildren: () =>
      import('@flowx/designer/modules/integration-designer/integration-designer-routes').then(
        (m) => m.IntegrationDesignerRoutes
      ),
  },
  {
    path: '',
    loadChildren: () =>
      import('@flowx/designer/modules/agent-builder/agent-builder-routes').then(
        (m) => m.AgentBuilderRoutes
      ),
  },
  {
    path: '',
    loadChildren: () =>
      import('@flowx/designer/modules/ontology/ontology-routes').then((m) => m.OntologyRoutes),
  },
  {
    path: '',
    loadChildren: () =>
      import('@flowx/designer/modules/regulatory-twin/regulatory-twin-routes').then(
        (m) => m.RegulatoryTwinRoutes
      ),
  },
  {
    path: '',
    loadComponent: () =>
      import('@flowx/designer/modules/merge-conflicts/components/merge-conflicts-outlet/merge-conflicts-outlet.component').then(
        (m) => m.FlxMergeConflictsOutletComponent
      ),
    loadChildren: () =>
      import('@flowx/designer/modules/merge-conflicts/merge-conflicts-routes').then(
        (m) => m.MergeConflictRoutes
      ),
    outlet: 'mergeConflicts',
  },
  {
    path: 'data-model',
    data: {
      ...resourcePage({ type: 'project-data-type' }),
      agent: AiAgent.ARCHITECT,
      location: 'PROJECTS->PROJECT_DATA_MODEL',
    },
    providers: [importProvidersFrom(EffectsModule.forFeature([fromStore.aiDataModelEffects]))],
    loadComponent: () =>
      import('@flowx/designer/modules/data-model-builder/components/project-data-model/project-data-model.component').then(
        ({ FlxProjectDataModelComponent }) => FlxProjectDataModelComponent
      ),
  },
  {
    path: 'documentation',
    data: { [LayoutOptions.HAS_SIDENAV]: true, location: 'PROJECTS->DOCUMENTATION' },
    loadComponent: () =>
      import('@flowx/designer/modules/documentation/documentation-browser.component').then(
        ({ DocumentationBrowserComponent }) => DocumentationBrowserComponent
      ),
  },
  {
    path: 'documentation-overview',
    data: { [LayoutOptions.HAS_SIDENAV]: true, location: 'PROJECTS->DOCUMENTATION_OVERVIEW' },
    loadComponent: () =>
      import('@flowx/designer/modules/documentation/project-doc-page.component').then(
        ({ ProjectDocPageComponent }) => ProjectDocPageComponent
      ),
  },
]
